import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { TechnicalViewService } from 'src/app/_services/technical-view.service';

@Component({
  selector: 'app-update-record',
  templateUrl: './update-record.component.html',
  styleUrls: ['./update-record.component.scss'],
})
export class UpdateRecordComponent implements OnInit {
  vehicle_id: any;
  chassis_no: any;
  data: any;
  loading = false;
  record_types: any[] = [
    { name: 'Brakes', value: 'brakes' },
    { name: 'Service', value: 'service' },
    { name: 'Tyres', value: 'tyres' },
    { name: 'Maintenance', value: 'maintenance' },
  ];
  selectedRecordType: any;

  // The dropdown/template *ngIf sections use lowercase keys, but the
  // established record_type convention already in the database is
  // capitalized and singular for tyres ("Brakes"/"Service"/"Tyre"/
  // "Maintenance"). A plain .toLowerCase() on an existing "Tyre" record
  // produces "tyre", which doesn't match this dropdown's "tyres" option -
  // the Record Type field then shows blank when editing any tyre record.
  private readonly recordTypeFromApi: Record<string, string> = {
    tyre: 'tyres',
  };
  private readonly recordTypeToApi: Record<string, string> = {
    brakes: 'Brakes',
    service: 'Service',
    tyres: 'Tyre',
    maintenance: 'Maintenance',
  };

  constructor(
    private technicalViewService: TechnicalViewService,
    private config: DynamicDialogConfig,
    public ref: DynamicDialogRef,
    private fb: FormBuilder,
    private messageService: MessageService
  ) {}

  technicalForm = this.fb.group({
    vehicle_reference: [''],
    odometer: [''],
    service_jobno: [''],
    service_brakes: [''],
    remarks: [''],
    record_type: [''],
    date_completed: [''],
    service_type: [''],
    tyre_pos: [''],
    tyre_size: [''],
    tyre_type: [''],
    vehicle_id: [''],
    technical_record_jobs: this.fb.array([]),
  });

  private mapRecordTypeFromApi(value: any): any {
    const lower = value?.toLowerCase?.();
    return lower ? this.recordTypeFromApi[lower] ?? lower : lower;
  }

  ngOnInit(): void {
    this.data = this.config.data;
    this.data.technical_record_jobs = this.data.technical_record_jobs || [];

    this.vehicle_id = this.data.vehicle_id;
    this.chassis_no = this.data.chassis_no;

    this.technicalForm.patchValue({
      vehicle_reference: this.data.vehicle_reference,
      odometer: this.data.odometer,
      service_jobno: this.data.service_jobno,
      service_brakes: this.data.service_brakes,
      remarks: this.data.remarks,
      record_type: this.mapRecordTypeFromApi(this.data.record_type),
      date_completed: this.data.date_completed,
      service_type: this.data.service_type,
      tyre_pos: this.data.tyre_pos,
      tyre_size: this.data.tyre_size,
      tyre_type: this.data.tyre_type,
      vehicle_id: this.data.vehicle_id,
      technical_record_jobs: this.data.technical_record_jobs,
    });

    this.technicalForm.get('record_type')?.valueChanges.subscribe((value) => {
      this.selectedRecordType = value;
    });

    this.data.technical_record_jobs.forEach((job: any) => {
      const technical_job = this.technicalViewService.getTechnicalJobForm();
      this.selectedRecordType = this.mapRecordTypeFromApi(this.data.record_type);

      technical_job.patchValue({
        id: job.id,
        job_description: job.job_description,
        remarks: job.remarks,
      });

      this.technicalJobArray.push(technical_job);
    });

    if (this.data.technical_record_jobs.length === 0) {
      this.addOrder();
    }

    this.selectedRecordType = this.mapRecordTypeFromApi(this.data.record_type);
  }

  get technicalJobArray() {
    return this.technicalForm.get('technical_record_jobs') as FormArray;
  }

  addOrder() {
    const technical_job = this.technicalViewService.getTechnicalJobForm();
    this.technicalJobArray.push(technical_job);
  }

  deleteOrder(index: number) {
    this.technicalJobArray.removeAt(index);
  }

  onChange($event: any) {
    this.selectedRecordType = $event.value;

    // Reset form values based on selected record type
    this.technicalForm.patchValue({
      service_jobno: '',
      service_brakes: '',
      remarks: '',
      service_type: '',
      tyre_pos: '',
      tyre_size: '',
      tyre_type: '',
      technical_record_jobs: [],
    });
  }

  cleanFormData(formData: any): any {
    if (Array.isArray(formData)) {
      // Clean each element in the array and filter out empty objects
      return formData
        .map((item) => this.cleanFormData(item))
        .filter((item) => Object.keys(item).length > 0);
    } else if (typeof formData === 'object' && formData !== null) {
      const cleanedData: any = {};
      for (const key in formData) {
        if (formData.hasOwnProperty(key)) {
          const value = formData[key];
          if (value !== '' && value !== null) {
            if (typeof value === 'object') {
              // Recursively clean nested objects or arrays
              const cleanedValue = this.cleanFormData(value);
              if (
                Object.keys(cleanedValue).length > 0 ||
                Array.isArray(value)
              ) {
                cleanedData[key] = cleanedValue;
              }
            } else {
              cleanedData[key] = value;
            }
          }
        }
      }
      return cleanedData;
    }
    return formData; // Return primitives as-is
  }

  onSubmit() {
    this.loading = true;

    const rawFormValue = this.technicalForm.getRawValue();

    const transformedJobs = this.technicalJobArray.value.map((job: any) => ({
      job_description: job.job_description?.value ?? job.job_description,
      remarks: job.remarks,
      id: job.id,
    }));

    const payload = {
      ...rawFormValue,
      record_type: rawFormValue.record_type
        ? this.recordTypeToApi[rawFormValue.record_type] ?? rawFormValue.record_type
        : rawFormValue.record_type,
      technical_record_jobs: transformedJobs,
    };

    const cleanedData = this.cleanFormData(payload);

    this.technicalViewService
      .updateTechnicalRecord(cleanedData, this.data.id)
      .subscribe(
        (res) => {
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: 'Technical record updated successfully',
            key: 'technical',
          });
          this.loading = false;
          this.ref.close(res);
        },
        () => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'An error occurred while updating the technical record',
            key: 'technical',
          });
          this.loading = false;
        }
      );
  }
}
